/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce, put } from '@redux-saga/core/effects'
import { FilterType, getFilter } from '@carto/api-client'
import { all, select, call, takeEvery } from 'redux-saga/effects'
import { addFilter } from '@carto/react-redux'
import {
  setViewPort,
  setViewStateDirect,
  addSource,
  removeFilter,
  removeSource,
  Source,
  addSpatialFilter
} from '@carto/react-redux'
import {
  ADD_FILTER_ACTION,
  removeWidget,
  removeWidgetsByDataSource,
  REMOVE_FILTER_ACTION,
  setWidgets,
  showGlobalBuilderError,
  newWidget,
  setSelectedWidgetId,
  removeFilterByWidgetRequest,
  setControlsPaneState,
  updateLayerFiltersForTimeSeriesAnimationRequest,
  addTimeSeriesDataSourceAnimating,
  removeTimeSeriesDataSourceAnimating,
  replaceWidget,
  updateWidgetProps,
  setTilesetWidgetSourceReady,
  getDataSourceFilters
} from './builderSlice'
import { PayloadAction } from '@reduxjs/toolkit'
import { updateTilesetDatasetTilejson } from './keplerGlActions'
import { ActionTypes } from '@carto/kepler.gl/actions'
import { Map, MapDataset } from 'features/maps/types'
import { SchemaField } from 'features/data-explorer/types'
import { CALCULATE_FEATURES_DEBOUNCE_TIME } from '../ui/builderConstants'
import { v4 as uuidv4 } from 'uuid'
import { FormulaWidget, Widget, WidgetType } from '..'
import { MapDatasetEnriched, RightPaneTab, KeplerTable } from '../types'
import {
  getTilejsonFromTilesetDataset,
  isServerSideMapFilteringSupported,
  getSpatialFiltersOptions,
  getSchemeFromTilesetDataset
} from './builderSliceUtils'
import { getEnrichedDatasetById, getkeplerDatasetById } from './keplerGlSlice'
import { getDatasetById as getMapDatasetById, updateCurrentMapDataset } from 'features/maps/state/mapsSlice'
import { getLayerLoader } from './builderSagas'
import { RequestStatus } from 'features/common'
import { updateDatasetColumnsFromMap } from './datasetSagas'
import { isWidgetValid } from '../model/widgetsValidator'
import { spatialDataTypeFromTilingSchemeTo } from '../utils/spatialDataSettings'
import { getValidatedSourceFilters, validateSourceFilters } from './widgetsUtils'
import { ProviderType } from 'features/connections/types'
import { canUseGlobal } from '../utils/widgetUtils'
import { loadWidgetSourceTiles, setTileFeatureExtractOptions } from './widgets'
import { getTilesetWidgetGeoColumnName } from '../utils/geoColumn'
import { getCrossFilteringDataSourcesByColumn, removeCrossFiltersBySource } from '../model/widgetFilters'
import { _Tile2DHeader } from '@deck.gl/geo-layers'
import { AppStore } from 'app/state/types'

const CROSS_FILTERING_FILTER_TYPES = Object.values(FilterType)

export function* loadDataForWidgets(source: Source & { accessToken?: string }, layerData?: KeplerTable) {
  try {
    {
      const keplerDataset = layerData || (yield select(getkeplerDatasetById, source.id))
      if (!keplerDataset) throw new Error(`dataset not found: ${source.id}`)

      const { spatialDataColumn, spatialDataType } = getSpatialFiltersOptions(keplerDataset)

      yield put(
        addSource({
          ...source,
          spatialDataColumn,
          spatialDataType
        })
      )
    }
  } catch (e: any) {
    console.error('loadDataForWidgets', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* removeDataForWidgets(datasetId: string) {
  try {
    yield put(removeWidgetsByDataSource(datasetId))
    yield put(removeSource(datasetId))
  } catch (e: any) {
    console.error('removeDataForWidgets', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* updateWidgetsGivenDatasetChanges(datasetId: string) {
  try {
    const dataset: MapDatasetEnriched = yield select((state) => getEnrichedDatasetById(state, datasetId))
    if (!dataset) return

    // To prevent edge-cases (e.g. Dataset might have changed their columns, some values are not valid anymore, ...) we clean the filters from all the widgets associated to the dataset, if cross-filtering is enabled
    removeCrossFiltersBySource(datasetId)

    // Dataset might have changed their columns, so we need to remove the widget column if it's not in the schema anymore
    yield call(removeWidgetColumnIfSchemaDoesNotContainColumnAnymore, datasetId, dataset.metadata?.schema)

    // If the new Dataset contains a column that could be used in cross-filtering, we need to update the cross-filtering widgets
    yield call(updateCrossFilteringWidgets)
  } catch (e: any) {
    console.error('updateWidgetsGivenDatasetChanges', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* removeWidgetColumnIfSchemaDoesNotContainColumnAnymore(datasetId: string, schema?: SchemaField[]) {
  try {
    const widgets: Widget[] = yield selectWidgets()
    const datasetWidgets = widgets.filter((w) => w.dataSource === datasetId)

    for (const widget of datasetWidgets) {
      if (!!schema && !schema.find((c) => c.name === widget.column)) {
        yield put(
          updateWidgetProps({ id: widget.id, props: { column: undefined, crossFilteringDataSourceIds: undefined } })
        )
      }
    }
  } catch (e: any) {
    console.error('removeWidgetColumnIfSchemaDoesNotContainColumnAnymore', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* updateCrossFilteringWidgets() {
  try {
    const widgets: Widget[] = yield selectWidgets()

    for (const widget of widgets) {
      // If there's no applicable column or Widget doesn't support cross-filtering, skip
      if (!widget.column || !('crossFilteringDataSourceIds' in widget)) continue

      // If the widget has no cross-filtering sources, skip
      if (!widget.crossFilteringDataSourceIds?.length) continue

      const crossFilteringDataSourceIds: string[] = getCrossFilteringDataSourcesByColumn({
        column: widget.column,
        widgetDataSourceId: widget.dataSource
      }).map((ds) => ds.id)

      yield put(replaceWidget({ ...widget, crossFilteringDataSourceIds }))
    }
  } catch (e: any) {
    console.error('updateCrossFilteringWidgets', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* applyCrossFilteringWidgetFilters(datasetId: string) {
  try {
    yield updateCrossFilteringWidgets()

    // Apply filters for specified Data Source
    const widgets: Widget[] = yield selectWidgets()

    for (const widget of widgets) {
      // If there's no applicable column or Widget doesn't support cross-filtering, skip
      if (!widget.column || !('crossFilteringDataSourceIds' in widget)) continue

      // If the widget doesn't have the datasetId in its crossFilteringDataSourceIds, skip
      if (!widget.crossFilteringDataSourceIds?.includes(datasetId)) continue

      for (const filterType of CROSS_FILTERING_FILTER_TYPES) {
        const filters = yield select(getDataSourceFilters, widget.dataSource)
        const filter = getFilter(filters || {}, { column: widget.column, type: filterType, owner: widget.id })
        if (filter) {
          yield put(
            addFilter({
              id: datasetId,
              column: widget.column,
              // @ts-expect-error TODO(c4r): Migrating types.
              type: filterType,
              values: filter.values,
              owner: filter.owner
            })
          )
        }
      }
    }
  } catch (e: any) {
    console.error('applyCrossFilteringWidgetFilters', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* removeCrossFilteringWidgetFilters(datasetId: string) {
  try {
    const widgets: Widget[] = yield selectWidgets()
    const dataSourceWidgets = widgets.filter((w) => w.dataSource === datasetId)
    if (!dataSourceWidgets.length) return

    for (const widget of dataSourceWidgets) {
      if (!widget.column || !('crossFilteringDataSourceIds' in widget)) continue

      const dataSourceIdsToRemoveFilters = widget.crossFilteringDataSourceIds?.filter((id) => id !== datasetId) || []

      for (const dataSourceId of dataSourceIdsToRemoveFilters) {
        yield put(removeFilter({ id: dataSourceId, column: widget.column }))
      }
    }
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* removeFilterByWidget(action: PayloadAction<Widget>) {
  try {
    const widget = action.payload
    const { dataSource, column: widgetColumn, id } = widget
    const dataset: MapDataset = yield select(getMapDatasetById, dataSource)
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)
    const filters = cartoDataSources[dataSource] && cartoDataSources[dataSource].filters
    const dataSourceIdsToRemoveFilters =
      'crossFilteringDataSourceIds' in widget && widget.crossFilteringDataSourceIds
        ? widget.crossFilteringDataSourceIds
        : [widget.dataSource]

    let widgetHasFilterApplied = false

    if (!filters) return
    if (!dataset) return
    if (!widgetColumn) return

    Object.entries(filters).map(([, columnData]) => {
      Object.entries(columnData).map(([, { owner }]) => {
        if (owner === id) {
          widgetHasFilterApplied = true
        }
      })
    })

    if (!widgetHasFilterApplied) return

    for (const dataSourceId of dataSourceIdsToRemoveFilters) {
      yield put(removeFilter({ id: dataSourceId, column: widgetColumn }))
    }
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export async function storeTilesForLocalWidgetsCalculation({
  datasetId,
  tiles,
  store
}: {
  datasetId: string
  tiles: _Tile2DHeader[]
  store: AppStore
}) {
  try {
    const dataset: MapDatasetEnriched = getEnrichedDatasetById(store.getState(), datasetId)
    if (!tiles) return

    const uniqueIdProperty = dataset.uniqueIdProperty
    const scheme = getSchemeFromTilesetDataset(dataset)
    const spatialDataType = spatialDataTypeFromTilingSchemeTo(scheme)
    const spatialDataColumn = getTilesetWidgetGeoColumnName(dataset)

    await setTileFeatureExtractOptions(datasetId, {
      uniqueIdProperty,
      spatialDataColumn,
      spatialDataType,
      storeGeometry: true
    })

    await loadWidgetSourceTiles(datasetId, tiles)

    store.dispatch(setTilesetWidgetSourceReady({ sourceId: datasetId, ready: true }))
  } catch (e: any) {
    console.error('storeTilesForLocalWidgetsCalculation', e)
    store.dispatch(showGlobalBuilderError(e.message))
  }
}

function* processNewWidget(action: PayloadAction<string>) {
  try {
    const widgets = yield selectWidgets()
    const sourceId = action.payload
    const source = yield select((state) => getEnrichedDatasetById(state, sourceId))

    const newWidget: FormulaWidget = {
      type: WidgetType.FORMULA,
      id: uuidv4(),
      title: `Widget ${widgets.length + 1}`,
      dataSource: sourceId,
      column: '',
      operation: 'count',
      global: canUseGlobal(source),
      isValid: true
    }

    yield put(setWidgets([...widgets, newWidget]))
    yield put(setSelectedWidgetId(newWidget.id))
    yield put(setControlsPaneState({ open: true, tab: RightPaneTab.Widgets }))
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* updateCartoViewState() {
  try {
    const mapState = yield select((state) => state.keplerGl?.map?.mapState)
    if (!mapState) return

    const viewState = {
      latitude: mapState.latitude,
      longitude: mapState.longitude,
      zoom: mapState.zoom,
      pitch: mapState.pitch,
      bearing: mapState.bearing,
      width: mapState.width,
      height: mapState.height,
      altitude: mapState.altitude,
      position: mapState.position
    }

    yield put(setViewStateDirect(viewState))
    yield put(setViewPort())
  } catch (error) {
    console.error('updateCartoViewportState failed', error)
  }
}

function* updateServerSideLayerFilters(datasetId: string) {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)
    const mapDataset: MapDataset = yield select(getMapDatasetById, datasetId)
    const widgets = yield select((state) => state.builder.widgets)
    const schema = yield select((state) => getkeplerDatasetById(state, datasetId)?.metadata?.schema)

    const filters = getValidatedSourceFilters(cartoDataSources[datasetId]?.filters, {
      widgets,
      schema,
      providerId: mapDataset.providerId
    })

    yield put(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: {
          status: RequestStatus.Pending
        }
      })
    )
    const loader = getLayerLoader({
      ...mapDataset,
      datasetId: datasetId,
      mapId: currentMap.id,
      provider: mapDataset.providerId,
      connection: mapDataset.connectionName,
      filters
    })
    const keplerDataset = yield loader.getKeplerDataset()
    const tilejson = getTilejsonFromTilesetDataset(keplerDataset)
    yield put(updateTilesetDatasetTilejson({ datasetId, tilejson }))
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* cleanServerSideLayerFilters(datasetId: string) {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const mapDataset: MapDataset = yield select(getMapDatasetById, datasetId)

    const loader = getLayerLoader({
      ...mapDataset,
      datasetId: datasetId,
      mapId: currentMap.id,
      provider: mapDataset.providerId,
      connection: mapDataset.connectionName,
      filters: undefined
    })
    const keplerDataset = yield loader.getKeplerDataset()
    const tilejson = getTilejsonFromTilesetDataset(keplerDataset)
    yield put(updateTilesetDatasetTilejson({ datasetId, tilejson }))
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* layersSupportServerSideMapFiltering(datasetId: string) {
  try {
    const source: Source | undefined = yield select((state) => state.carto.dataSources[datasetId])

    const isTimeSeriesAnimationRunning = (yield select((state) => state.builder.timeSeriesDataSourceAnimating))[
      datasetId
    ]
    return source && !isTimeSeriesAnimationRunning && isServerSideMapFilteringSupported({ source })
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* updateLayerFilters(action: PayloadAction<{ id: string }>) {
  const { id } = action.payload
  const isServerSideSupported = yield layersSupportServerSideMapFiltering(id)

  if (isServerSideSupported) {
    yield updateServerSideLayerFilters(id)
  }
}

function* updateLayerFiltersForTimeSeriesAnimation(action: PayloadAction<{ datasetId: string; isRunning: boolean }>) {
  try {
    const { datasetId, isRunning } = action.payload
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)

    if (!isServerSideMapFilteringSupported({ source: cartoDataSources[datasetId] })) {
      return
    }

    if (isRunning) {
      yield put(addTimeSeriesDataSourceAnimating(datasetId))
      yield cleanServerSideLayerFilters(datasetId)
    } else {
      yield put(removeTimeSeriesDataSourceAnimating(datasetId))
    }
  } catch (e: any) {
    console.error(e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* loadWidgetsMapConfig(currentMap: Map) {
  try {
    const widgets = currentMap.keplerMapConfig?.config?.widgets
    const spatialFilter = currentMap.keplerMapConfig?.config?.spatialFilter

    if (widgets) {
      yield put(setWidgets(widgets))
    }

    if (spatialFilter) {
      yield put(addSpatialFilter({ geometry: spatialFilter }))
    }
  } catch (e: any) {
    console.error('loadWidgetsMapConfig', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* validateWidgetsConfig(datasetId: string) {
  try {
    const dataset: MapDatasetEnriched = yield select((state) => getEnrichedDatasetById(state, datasetId))
    const widgets: Widget[] = yield select((state) => state.builder.widgets)

    for (const widget of widgets) {
      if (widget.dataSource !== datasetId) {
        continue
      }
      if (!isWidgetValid(widget, { source: dataset })) {
        yield put(updateWidgetProps({ id: widget.id, props: { isValid: false } }))
      } else if (!widget.isValid) {
        yield put(updateWidgetProps({ id: widget.id, props: { isValid: true } }))
      }
    }
  } catch (error) {
    console.error('validateWidgetsConfig failed', error)
  }
}

export function* validateDatasetFilters(datasetId: string) {
  try {
    const widgets: Widget[] = yield select((state) => state.builder.widgets)
    const keplerDataset: KeplerTable | undefined = yield select((state) => getkeplerDatasetById(state, datasetId))
    const cartoSource: Source | undefined = yield select((state) => state.carto.dataSources[datasetId])

    if (!keplerDataset || !cartoSource || !cartoSource.filters) return

    const filters = cartoSource.filters

    const [filtersValid, validFilters] = validateSourceFilters(filters, {
      widgets,
      schema: keplerDataset.metadata?.schema,
      providerId: cartoSource.provider as unknown as ProviderType
    })
    if (filtersValid) return

    yield put(addSource({ ...cartoSource, filters: validFilters }))
  } catch (e) {
    console.error('updateFilters failed', e)
  }
}

function* selectWidgets() {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  return yield select((state) => state.builder.widgets)
}

function* syncDatasetsColumnsWithMapWidgets(action: PayloadAction<{ id: string }>) {
  try {
    const { id: widgetId } = action.payload

    const datasetId = yield select((state) => state.builder.widgets.find((w) => w.id === widgetId)?.dataSource)
    if (datasetId) {
      yield updateDatasetColumnsFromMap(datasetId)
    }
  } catch (e: any) {
    console.error('syncDatasetsColumnsAfterWidgetsChange', e)
    const msg = `Error updating columns. ${e.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

function* watchMap() {
  yield debounce(CALCULATE_FEATURES_DEBOUNCE_TIME, ActionTypes.UPDATE_MAP, updateCartoViewState)
  yield takeEvery(ActionTypes.ADD_DATA_TO_MAP, updateCartoViewState)

  yield takeEvery(removeWidget.type, removeFilterByWidget)
  yield takeEvery(removeFilterByWidgetRequest.type, removeFilterByWidget)
  yield takeEvery(ADD_FILTER_ACTION, updateLayerFilters)
  yield takeEvery(REMOVE_FILTER_ACTION, updateLayerFilters)
  yield takeEvery([newWidget.type], processNewWidget)
  yield takeEvery([updateLayerFiltersForTimeSeriesAnimationRequest.type], updateLayerFiltersForTimeSeriesAnimation)
  yield takeEvery([replaceWidget.type, updateWidgetProps.type, removeWidget.type], syncDatasetsColumnsWithMapWidgets)
}

export default function* widgetsSaga() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
